import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import CompanyFeedback from "./pages/CompanyFeedback";
import HelpDesk from "./pages/HelpDesk";
import NewFeedback from "./pages/NewFeedback";
import AdminDashboard from "./pages/AdminDashboard";
import Redeem from "./pages/Redeem";
import EmbeddedChat from "./pages/embed/chat";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import NewBlogPost from "./pages/NewBlogPost";
import Sitemap from "./pages/Sitemap";
import Preview from "./pages/Preview";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import WhopLicense from "./pages/WhopLicense";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Company } from "@/types";
import CompanyMetadata from "@/components/shared/CompanyMetadata";

const queryClient = new QueryClient();

// List of domains that should be treated as main app domains, not custom company domains
const MAIN_APP_DOMAINS = ['feednatic.com', 'www.feednatic.com', 'changemyproduct.com'];

const App = () => {
  const [customDomainCompany, setCustomDomainCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isHelpSubdomain, setIsHelpSubdomain] = useState(false);

  useEffect(() => {
    const checkCustomDomain = async () => {
      try {
        const hostname = window.location.hostname;
        console.log('Current hostname:', hostname);

        // Special case for feedback.feednatic.com and help.feednatic.com
        if (hostname === 'feedback.feednatic.com' || hostname === 'help.feednatic.com') {
          const isHelp = hostname === 'help.feednatic.com';
          console.log(`On ${isHelp ? 'help' : 'feedback'} subdomain for feednatic.com`);
          
          // Get the feednatic company
          const { data: company, error } = await supabase
            .from('companies')
            .select('*')
            .eq('slug', 'feednatic')
            .single();

          // Check for company fetch error
          if (error) {
            console.error('Error fetching feednatic company:', error);
          }

          if (company) {
            console.log('Found feednatic company:', company);
            setCustomDomainCompany(company);
            setIsHelpSubdomain(isHelp);
            setIsLoading(false);
            return;
          }
        }

        // Check if we're on localhost or one of the main app domains
        if (hostname === 'localhost' || 
            hostname === '127.0.0.1' || 
            MAIN_APP_DOMAINS.includes(hostname)) {
          console.log('On main app domain or localhost, rendering main app');
          setIsLoading(false);
          return;
        }

        let domainToCheck = hostname;
        let isHelp = false;
        
        if (hostname.startsWith('feedback.')) {
          domainToCheck = hostname.substring(9);
        }
        else if (hostname.startsWith('help.')) {
          domainToCheck = hostname.substring(5);
          isHelp = true;
        }
        
        console.log('Looking up domain:', domainToCheck);

        const { data: company, error } = await supabase
          .from('companies')
          .select('*')
          .eq('custom_domain', domainToCheck)
          .in('plan', ['starter', 'enterprise'])
          .single();

        if (error) {
          console.error('Error fetching company by domain:', error);
        }

        if (company) {
          console.log('Found company with matching domain:', company);
          setCustomDomainCompany(company);
          setIsHelpSubdomain(isHelp);
        } else {
          console.log('No company found with custom domain:', domainToCheck);
          
          if (domainToCheck.startsWith('www.')) {
            const domainWithoutWww = domainToCheck.substring(4);
            console.log('Trying without www:', domainWithoutWww);
            
            // Skip check if it's one of our main domains without www
            if (MAIN_APP_DOMAINS.includes(domainWithoutWww)) {
              console.log('On main app domain without www, rendering main app');
              setIsLoading(false);
              return;
            }
            
            const { data: companyWithoutWww } = await supabase
              .from('companies')
              .select('*')
              .eq('custom_domain', domainWithoutWww)
              .in('plan', ['starter', 'enterprise'])
              .single();
              
            if (companyWithoutWww) {
              console.log('Found company with matching domain (without www):', companyWithoutWww);
              setCustomDomainCompany(companyWithoutWww);
              setIsHelpSubdomain(isHelp);
            }
          }
        }
      } catch (error) {
        console.error('Error in checkCustomDomain:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkCustomDomain();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Strict validation of company data to prevent metadata errors
  const hasValidCompanyData = customDomainCompany && 
    typeof customDomainCompany === 'object' && 
    typeof customDomainCompany.name === 'string' && 
    customDomainCompany.name.trim().length > 0;

  if (hasValidCompanyData) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          {hasValidCompanyData && (
            <CompanyMetadata 
              company={customDomainCompany} 
              pageType={isHelpSubdomain ? "help" : "feedback"} 
            />
          )}
          <BrowserRouter>
            <Routes>
              {/* Add the /feedback/new route for custom domains */}
              {!isHelpSubdomain && (
                <Route 
                  path="/feedback/new" 
                  element={<NewFeedback customSlug={customDomainCompany.slug} />}
                />
              )}
              <Route 
                path="/*" 
                element={
                  isHelpSubdomain ? 
                  <HelpDesk customSlug={customDomainCompany.slug} /> : 
                  <CompanyFeedback customSlug={customDomainCompany.slug} />
                } 
              />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/license" element={<WhopLicense />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/blog/new" element={<NewBlogPost />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/sitemap.xml" element={<Sitemap />} />
            <Route path="/redeem" element={<Redeem />} />
            <Route path="/preview" element={<Preview />} />
            <Route path="/embed/chat" element={<EmbeddedChat />} />
            <Route path="/:slug" element={<CompanyFeedback />} />
            <Route path="/:slug/help" element={<HelpDesk />} />
            <Route path="/:slug/feedback/new" element={<NewFeedback />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
